// The wizard asks the back end to price an order (POST /api/pricing/quote) and shows exactly what comes back. The back
// end prices with the same code as the invoice, so the figures here are the ones the client will be invoiced and charged.
import { ordersOwnIxbrl } from './ownIxbrl';
import { orderedServiceIds } from './taxComputation';

export const MAX_QUOTE_COMPANIES = 50;   // the most companies one order can hold

// Where the order summary leaves the options chosen so far, for the quote page to price.
export const QUOTE_INPUT_KEY = 'ixbrl_quote_input';

/**
 * The request for the order as chosen so far, or null while there is nothing to price (no account type chosen yet).
 * Before companies are chosen (step 1) it prices `placeholderCount` unnamed companies of the chosen type.
 */
export const buildQuoteRequest = (wizardData, placeholderCount = 1) => {
  if (!wizardData) return null;
  const own = ordersOwnIxbrl(wizardData);
  const defaultType = wizardData.config?.accountType || null;
  const chosen = wizardData.selectedCompanies || [];
  const accountTypeOf = (company) => (own ? null : company?.accountType || defaultType);

  const companies = chosen.length > 0
    ? chosen.map((company) => ({ id: String(company.id), name: company.name || 'Company', account_type: accountTypeOf(company) }))
    : Array.from({ length: Math.min(MAX_QUOTE_COMPANIES, Math.max(1, placeholderCount)) }, (_, index) => ({
      id: `company-${index + 1}`,
      name: `Company ${index + 1}`,
      account_type: accountTypeOf(null),
    }));

  if (companies.some((company) => !own && !company.account_type)) return null;
  return {
    companies,
    services: orderedServiceIds(wizardData),
    delivery_time: wizardData.config?.deliveryTime === 'express' ? 'express' : 'standard',
  };
};

/**
 * The quote's lines grouped for the order summary: tagging by account type, each filing service, and express, each with
 * how many companies it covers and what it comes to, plus the order's subtotal, volume discount and total.
 */
export const summariseQuote = (quote) => {
  const groups = new Map();
  (quote?.companies || []).forEach((company) => {
    company.lines.forEach((line) => {
      const label = line.kind === 'tagging' ? line.detail : line.service;
      const key = `${line.kind}|${label}`;
      const group = groups.get(key) || { kind: line.kind, label, count: 0, amount: 0 };
      group.count += 1;
      group.amount += line.amount_pence;
      groups.set(key, group);
    });
  });
  const withUnit = (group) => ({ ...group, unit: group.count ? group.amount / group.count : 0 });
  const ofKind = (kind) => [...groups.values()].filter((group) => group.kind === kind).map(withUnit);
  return {
    tagging: ofKind('tagging'),
    filing: ofKind('filing'),
    express: ofKind('express')[0] || null,
    subtotal: quote?.subtotal_pence || 0,
    discount: quote?.discount_pence || 0,
    discountPercent: quote?.discount_percent || 0,
    total: quote?.total_pence || 0,
    companyCount: quote?.company_count || 0,
  };
};

// A cell that starts with = + - @ tab or CR runs as a formula in Excel and Sheets. Prefix a quote so it stays text.
const csvCell = (value) => {
  const text = value == null ? '' : String(value);
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

export const toCsv = (rows) => rows.map((row) => row.map(csvCell).join(',')).join('\r\n');

export const penceToDecimal = (pence) => ((pence || 0) / 100).toFixed(2);
